import {
  type ApiResponse,
  HTTPMethod,
  request,
} from '../../global/api/legacyApi'
import { type CommentDTO } from '../models/Comment'

interface ListCommentsParams {
  /** Limit to a specific comment. */
  comment_id: number
  /** Limit to a specific comment type. Default: (empty). Valid: (empty), chat, question and comment. */
  comment_type: 'chat' | 'question' | 'comment' | 'speaker-chat'
  /** List comments by a specific user. */
  comment_user_id: number
  /** Include channel for realtime updates in response. */
  include_realtime_p: boolean
  /** Comments to specific object. Valid: photo_id and album_id. */
  object_id: number
  /** Comments to specific object type. Default: (empty) Valid: (empty), photo and album. */
  object_type: 'photo' | 'album' | 'live'
  /** Order of the comments in the response. Comments are always ordered by creation date. Default: desc Valid : desc and asc. */
  order: 'asc' | 'desc'
  /** Page offset for the request. */
  p: number
  /** Include webinar presenter token if only authenticated through a webinar join link. */
  presenter_token: string
  /** Search through comments. */
  search: string
  /** Number of objects to include in the response. */
  size: number
  /** Show promoted comments first */
  prioritize_promoted_p: boolean
  /** Filter only promoted or non-promoted comments */
  promoted_p: boolean
  /** Include reaction information in the comments */
  include_reactions_p: boolean
  /** Include information about which comments are in reply to other comments */
  include_reply_to_comments_p: boolean
}

const listComments = (params: Partial<ListCommentsParams>) =>
  request<ApiResponse<CommentDTO[]>>({
    url: 'api/2/comment/list',
    method: HTTPMethod.GET,
    data: { ...params },
  })

interface DeleteCommentParams {
  comment_id: number
}

const deleteComments = (params: DeleteCommentParams) =>
  request({
    url: '/api/2/comment/delete',
    method: HTTPMethod.POST,
    data: params,
  })

interface AddCommentParams {
  /** Identifier of the object you want to add a comment to. Valid: photo_id of a photo or video object. */
  object_id: number
  /** The type of object for which you are adding a comment. Valid: live and photo. */
  object_type: 'photo' | 'album' | 'live'
  /** Optional token for the object to comment on, if the object is not public. */
  object_token?: string
  /** The name of the poster of the comment. */
  name?: string
  /** The email address of the poster of the comment. */
  email?: string
  /** The content of the comment to be posted. */
  content?: string
  /** URL to attach to comment. */
  url?: string
  /** The IP address of the poster of the comment. This is used for spam protection and learning. Permission: write and higher. */
  ip_address?: string
  /** An optional floating number to indicate the comment's position relative to the video's timeline. */
  comment_time?: number
  /** Type of comment. Default: comment. Valid: comment, question and chat. */
  comment_type?: 'comment' | 'question' | 'chat' | 'speaker-chat'
  /** UUID for commenter */
  uuid?: string
  /** File attachment for comment */
  file?: string
  /** File attachment for comment */
  ['file.tmpfile']?: string
  /** Include webinar presenter token if only authenticated through a webinar join link. */
  presenter_token?: string
  /** Reply to an existing comment */
  reply_to_comment_id?: number
}

const addComment = (params: AddCommentParams) =>
  request<ApiResponse<{ comment_id: number }>>({
    url: '/api/2/comment/add',
    method: HTTPMethod.POST,
    data: params,
  })

interface PromoteCommentParams {
  comment_id: number
  promoted_p: boolean
}

const promoteComment = (params: PromoteCommentParams) =>
  request({
    url: '/api/2/comment/promote',
    method: HTTPMethod.POST,
    data: params,
  })

interface CloneCommentParams {
  clone_comment_type: 'question' | 'chat' | 'comment'
  comment_id: number
}

const cloneComment = (params: CloneCommentParams) =>
  request({
    url: '/api/2/comment/clone',
    method: HTTPMethod.POST,
    data: params,
  })

interface CommentReactionParams {
  object_id: number
  object_token?: string
  object_type: 'photo' | 'album' | 'live'
  comment_id: number
  reaction_emoji: string
  uuid?: string
  presenter_token?: string
}

const addCommentReaction = (params: CommentReactionParams) =>
  request({
    url: '/api/2/comment/reaction/add',
    method: HTTPMethod.POST,
    data: params,
  })

const removeCommentReaction = (params: CommentReactionParams) =>
  request({
    url: '/api/2/comment/reaction/remove',
    method: HTTPMethod.POST,
    data: params,
  })

interface ListCommentReactionsParams {
  object_id: number
  object_token: string
  object_type: 'photo' | 'album' | 'live'
}

const listCommentReactions = (params: ListCommentReactionsParams) =>
  request<ApiResponse<{ comment_id: number; reaction_emoji: string }[]>>({
    url: '/api/2/comment/reaction/list',
    method: HTTPMethod.POST,
    data: params,
  })

export {
  listComments,
  deleteComments,
  addComment,
  promoteComment,
  cloneComment,
  addCommentReaction,
  removeCommentReaction,
  listCommentReactions,
}
